import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/pavimento.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const SolicitacaoImagemManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_solicitacao_imagem: '',
    id_imagem: '',
    id_solicitacao: '',
    descr_solicitacao_imagem: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [imagemSearch, setImagemSearch] = useState('');
  const [solicitacaoSearch, setSolicitacaoSearch] = useState('');
  const [imagens, setImagens] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchImagens = useCallback(async () => {
    try {
      const response = await api.get('/imagem');
      setImagens(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar imagens:', error);
    }
  }, []);

  const fetchSolicitacoes = useCallback(async () => {
    try {
      const response = await api.get('/solicitacao');
      setSolicitacoes(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
    }
  }, []);

  const imagensFiltradas = useMemo(() => {
    if (!imagemSearch) return imagens;
    const searchLower = imagemSearch.toLowerCase();
    return imagens.filter(i =>
      String(i.id_imagem).toLowerCase().includes(searchLower) ||
      (i.descricao && i.descricao.toLowerCase().includes(searchLower)) ||
      (i.nome_externo && i.nome_externo.toLowerCase().includes(searchLower))
    );
  }, [imagens, imagemSearch]);

  const solicitacoesFiltradas = useMemo(() => {
    if (!solicitacaoSearch) return solicitacoes;
    const searchLower = solicitacaoSearch.toLowerCase();
    return solicitacoes.filter(s =>
      String(s.id_solicitacao).toLowerCase().includes(searchLower) ||
      (s.descr_solicitacao && s.descr_solicitacao.toLowerCase().includes(searchLower))
    );
  }, [solicitacoes, solicitacaoSearch]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/solicitacao-imagem');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchImagens();
    fetchSolicitacoes();
  }, [verificarAPI, fetchImagens, fetchSolicitacoes]);

  const atualizarRegistro = useCallback(async (id_solicitacao_imagem, id_imagem, id_solicitacao, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'imagem da solicitação', { id_solicitacao_imagem, id_imagem, id_solicitacao, dadosAtualizacao });
    try {
      const response = await api.put(`/solicitacao-imagem/${id_solicitacao_imagem}/${id_imagem}/${id_solicitacao}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'imagem da solicitação', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'imagem da solicitação');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id_solicitacao_imagem, id_imagem, id_solicitacao) => {
    logarRequisicao('Excluir', 'imagem da solicitação', { id_solicitacao_imagem, id_imagem, id_solicitacao });
    try {
      const response = await api.delete(`/solicitacao-imagem/${id_solicitacao_imagem}/${id_imagem}/${id_solicitacao}`);
      logarSucesso('Excluir', 'imagem da solicitação', response.data);
      const novosDados = dados.filter(item => !(item.id_solicitacao_imagem === id_solicitacao_imagem && item.id_imagem === id_imagem && item.id_solicitacao === id_solicitacao));
      setDados(novosDados);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'imagem da solicitação');
      mostrarMensagem('error', mensagemErro);
    }
  }, [dados, mostrarMensagem]);

  const getColunas = useCallback(() => {
    const colunasBase = [
      {
        title: 'ID solicitacao imagem',
        field: 'id_solicitacao_imagem',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'ID Imagem',
        field: 'id_imagem',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'ID solicitação',
        field: 'id_solicitacao',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'Descrição',
        field: 'descr_solicitacao_imagem',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_solicitacao_imagem, data.id_imagem, data.id_solicitacao, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro?`)) {
            excluirRegistro(data.id_solicitacao_imagem, data.id_imagem, data.id_solicitacao);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({ ...col, editor: 'input' }))
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/solicitacao-imagem');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas(),
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_solicitacao_imagem' // Set the unique id for each row
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas());
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, getColunas, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_solicitacao_imagem, id_imagem, id_solicitacao, descr_solicitacao_imagem } = formData;

    const camposFaltando = [];
    if (!id_solicitacao_imagem) camposFaltando.push('ID Solicitacao Imagem');
    if (!id_imagem) camposFaltando.push('ID Imagem');
    if (!id_solicitacao) camposFaltando.push('ID Solicitacao');
    if (!descr_solicitacao_imagem) camposFaltando.push('Descrição');

    if (camposFaltando.length > 0) {
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')}`);
      return;
    }

    const dataToSend = {
      id_solicitacao_imagem,
      id_imagem,
      id_solicitacao,
      descr_solicitacao_imagem: descr_solicitacao_imagem === '' ? null : descr_solicitacao_imagem,
    };

    logarRequisicao('Adicionar', 'imagem da solicitação', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/solicitacao-imagem', dataToSend);
      logarSucesso('Adicionar', 'imagem da solicitação', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_solicitacao_imagem: '', id_imagem: '', id_solicitacao: '', descr_solicitacao_imagem: '' });
      setImagemSearch('');
      setSolicitacaoSearch('');
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'imagem da solicitação');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'solicitacao_imagem.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Imagens de Solicitação</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_solicitacao_imagem</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/solicitacao imagem.mov" buttonText="Ver Tutorial Solicitação Imagem" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}


      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_solicitacao_imagem">ID Solicitacao Imagem:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_solicitacao_imagem" name="id_solicitacao_imagem" value={formData.id_solicitacao_imagem} onChange={handleInputChange} placeholder="Ex: SI01" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_imagem">ID Imagem:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input
              type="text"
              list="imagens-list"
              value={imagemSearch}
              onChange={(e) => {
                setImagemSearch(e.target.value);
                const match = imagens.find(i => `${i.id_imagem} - ${i.descricao || i.nome_externo || ''}` === e.target.value);
                if (match) {
                  setFormData(prev => ({ ...prev, id_imagem: match.id_imagem }));
                }
              }}
              placeholder="Buscar imagem..."
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
            />
            <datalist id="imagens-list">
              {imagensFiltradas.map(i => (
                <option key={i.id_imagem} value={`${i.id_imagem} - ${i.descricao || i.nome_externo || ''}`} />
              ))}
            </datalist>
            <input type="hidden" name="id_imagem" value={formData.id_imagem} />
          </div>
          <div className="form-group">
            <label htmlFor="id_solicitacao">ID Solicitacao:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input
              type="text"
              list="solicitacoes-list"
              value={solicitacaoSearch}
              onChange={(e) => {
                setSolicitacaoSearch(e.target.value);
                const match = solicitacoes.find(s => `${s.id_solicitacao} - ${s.descr_solicitacao || ''}` === e.target.value);
                if (match) {
                  setFormData(prev => ({ ...prev, id_solicitacao: match.id_solicitacao }));
                }
              }}
              placeholder="Buscar solicitação..."
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
            />
            <datalist id="solicitacoes-list">
              {solicitacoesFiltradas.map(s => (
                <option key={s.id_solicitacao} value={`${s.id_solicitacao} - ${s.descr_solicitacao || ''}`} />
              ))}
            </datalist>
            <input type="hidden" name="id_solicitacao" value={formData.id_solicitacao} />
          </div>
          <div className="form-group">
            <label htmlFor="descr_solicitacao_imagem">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="descr_solicitacao_imagem" name="descr_solicitacao_imagem" value={formData.descr_solicitacao_imagem} onChange={handleInputChange} placeholder="Ex: Imagem da solicitacao" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          {/* Linha única: Adicionar, Recarregar, Exportar CSV + aviso de obrigatórios */}
          <div
            className="actions-row"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}
          >
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>


      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default SolicitacaoImagemManager;